import Head from "next/head";

import { Article } from "@/types/models";
import { LangstonContent } from "@/types/models/Content.interface";
import contentTypes from "@/view/constants/contentTypes";

export interface ContentHeadProps {
  title: string | undefined;
  teaser: string | undefined;
  imageUrl: string | undefined;
  isAllowedInSearch: boolean;
}

type GetContentHeadSEOData = (
  contentData: LangstonContent,
  contentMetadata: Metadata[],
  currentLevel: string,
) => {
  title: string | null;
  teaser: string | null;
  imageUrl: string | null;
  isAllowedInSearch: boolean;
};

export interface Metadata {
  data: {
    content_type: string;
    allowed_in_search: boolean;
  };
}

export const isContentAllowedInSearch: (metadata: Metadata[]) => boolean = (
  metadata: Metadata[] = [],
) => {
  const providerMetadata = metadata.find(
    (tag) => tag?.data?.content_type === "PROVIDER",
  );
  return providerMetadata?.data?.allowed_in_search ?? true;
};

export const getContentHeadSEOData: GetContentHeadSEOData = (
  contentData: LangstonContent,
  contentMetadata: Metadata[],
  currentLevel: string | null,
) => {
  const isAllowedInSearch = isContentAllowedInSearch(contentMetadata);
  if (contentData.content_type === contentTypes.ARTICLE) {
    let selectedArticleLevel = (contentData as Article).article_levels?.find(
      (level) => level?.id === currentLevel,
    );
    if (!selectedArticleLevel) {
      selectedArticleLevel = (contentData as Article).article_levels?.[0];
    }
    return {
      title: selectedArticleLevel?.title || null,
      teaser: selectedArticleLevel?.teaser || null,
      imageUrl: (contentData as Article)?.header_image?.url || null,
      isAllowedInSearch,
    };
  }
  // TODO: Implement meta tags for interactive videos
  return {
    title: null,
    teaser: null,
    imageUrl: null,
    isAllowedInSearch,
  };
};

const ContentHead = ({
  title,
  teaser,
  imageUrl,
  isAllowedInSearch,
}: ContentHeadProps) => {
  return (
    <Head>
      {title && <title>{title}</title>}
      {teaser && <meta name="description" content={teaser} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {!isAllowedInSearch && <meta name="robots" content="noindex" />}
    </Head>
  );
};
export default ContentHead;
